import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { reserveUsername } from '../services/usernameService';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string, nationality?: string, language?: string) => Promise<void>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      if (user) {
      } else {
      }
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    if (!user.emailVerified) {
      // Don't sign out - just throw error to keep page state
      throw new Error('EMAIL_NOT_VERIFIED');
    }
  };

  const register = async (email: string, password: string, username: string, nationality?: string, language?: string) => {
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Send verification email
    await sendEmailVerification(user);
    
    // Reserve username in usernames collection
    await reserveUsername(username, user.uid);
    
    // Detect defaults if not provided
    const detectedLocale = navigator.language || 'en-US';
    const detectedLanguage = language || detectedLocale.split('-')[0]; // e.g., 'en' from 'en-US'
    const detectedNationality = nationality || (detectedLocale.split('-')[1] || 'US'); // e.g., 'US' from 'en-US'
    
    // Create initial user profile in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    await setDoc(userDocRef, {
      email: user.email,
      username: username.trim(),
      usernameLowercase: username.trim().toLowerCase(),
      emailVerified: false,
      nationality: detectedNationality.toUpperCase(), // ISO 3166-1 alpha-2 (e.g., 'GB', 'US', 'FR')
      language: detectedLanguage.toLowerCase(), // ISO 639-1 (e.g., 'en', 'es', 'fr')
      typingContentLanguage: detectedLanguage.toLowerCase(), // Inherit from UI language
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
  };

  const sendVerificationEmail = async () => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    if (currentUser.emailVerified) {
      throw new Error('Email already verified');
    }
    await sendEmailVerification(currentUser);
  };

  const logout = async () => {
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const deleteAccount = async () => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    // Delete user data from Firestore
    await deleteDoc(doc(db, 'users', currentUser.uid));
    
    // Delete the Firebase Auth account
    await deleteUser(currentUser);
  };

  const value: AuthContextType = {
    currentUser,
    loading,
    login,
    register,
    logout,
    sendVerificationEmail,
    resetPassword,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
